/**
 * Entra ID sign-in and token acquisition.
 *
 * Authorization code flow with PKCE, which is what a single-page app should use — no
 * client secret ever reaches the browser. Tokens are held in session storage so closing
 * the tab ends the session, which is the right default for a tool holding HR data on a
 * shared or hot-desked machine.
 *
 * Local development can run without a tenant (VITE_AUTH_DISABLED), so the app is workable
 * before the app registrations exist. That flag must match the API's own AUTH_DISABLED,
 * and the API refuses it outright in production.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

const env = import.meta.env;
export const authDisabled = env.VITE_AUTH_DISABLED === 'true';

const msalConfig = {
  auth: {
    clientId: env.VITE_ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${env.VITE_ENTRA_TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
};

const apiRequest = { scopes: [env.VITE_API_SCOPE].filter(Boolean) };

export const msalInstance = authDisabled ? null : new PublicClientApplication(msalConfig);

/**
 * A bearer token for the API, refreshed silently when possible and falling back to an
 * interactive prompt only when the tenant genuinely requires one (consent, MFA, expiry).
 */
export async function getAccessToken() {
  if (authDisabled) return null;

  const account = msalInstance.getAllAccounts()[0];
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(apiRequest);
      return null;
    }
    throw err;
  }
}

const AuthContext = createContext({ user: null, ready: false, signIn: () => {}, signOut: () => {} });

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [ready, setReady] = useState(authDisabled);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    if (authDisabled) return;
    msalInstance
      .initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then((result) => {
        const active = result?.account ?? msalInstance.getAllAccounts()[0] ?? null;
        if (active) msalInstance.setActiveAccount(active);
        setAccount(active);
        setReady(true);
      })
      .catch(() => setReady(true));
  }, []);

  const value = {
    ready,
    account,
    signIn: () => msalInstance?.loginRedirect(apiRequest),
    signOut: () => msalInstance?.logoutRedirect(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Blocks the app until there is a signed-in account. */
export function RequireAuth({ children }) {
  const { ready, account, signIn } = useAuth();

  if (authDisabled) return children;
  if (!ready) return <div className="auth-screen">Signing in…</div>;
  if (account) return children;

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-wordmark">
          Capaci<span>TEA</span>
        </div>
        <p>Workforce Capacity · The Energy Authority</p>
        <button className="btn btn-primary" onClick={signIn}>
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
}
